import {
  authorizeUrl,
  challengeFor,
  codeExchangeBody,
  isExpired,
  logoutUrl,
  parseTokenResponse,
  randomToken,
  refreshBody,
  type SiteConfig,
  type Tokens,
  tokenUrl,
} from "./oauth.ts";
import { SignInError, signInFailure } from "./sign-in-errors.ts";

/**
 * Tokens live in `sessionStorage`: they survive a page reload but not the tab,
 * and nothing on another origin can read them. A script injected into this
 * origin could, which is what the site's content security policy is for.
 */
const TOKENS = "phone-booth.tokens";
const VERIFIER = "phone-booth.verifier";
const STATE = "phone-booth.state";

/** Registered with Cognito as a callback URL, so it has to be the bare origin. */
export function redirectUri(): string {
  return `${window.location.origin}/`;
}

function readTokens(): Tokens | null {
  const raw = sessionStorage.getItem(TOKENS);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Tokens;
  } catch {
    return null;
  }
}

function writeTokens(tokens: Tokens | null): void {
  if (tokens) sessionStorage.setItem(TOKENS, JSON.stringify(tokens));
  else sessionStorage.removeItem(TOKENS);
}

function forgetFlow(): void {
  sessionStorage.removeItem(VERIFIER);
  sessionStorage.removeItem(STATE);
}

async function postToken(config: SiteConfig, body: URLSearchParams): Promise<Tokens> {
  const response = await fetch(tokenUrl(config), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!response.ok) throw new Error(`sign-in failed (${response.status})`);
  return parseTokenResponse(await response.json(), Date.now());
}

/** Sends the browser to Google. Nothing after this call runs. */
export async function signIn(config: SiteConfig): Promise<void> {
  const verifier = randomToken();
  const state = randomToken();
  sessionStorage.setItem(VERIFIER, verifier);
  sessionStorage.setItem(STATE, state);
  window.location.assign(
    authorizeUrl(config, {
      redirectUri: redirectUri(),
      challenge: await challengeFor(verifier),
      state,
    }),
  );
}

export function signOut(config: SiteConfig): void {
  writeTokens(null);
  forgetFlow();
  window.location.assign(logoutUrl(config, redirectUri()));
}

/**
 * Finishes the redirect back from Google, if that is what this load is. The
 * code is swapped for tokens and taken out of the address bar, so a reload
 * cannot try to spend it twice.
 */
export async function completeSignIn(config: SiteConfig): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const failure = params.get("error");
  if (!code && !failure) return;

  const verifier = sessionStorage.getItem(VERIFIER);
  const expectedState = sessionStorage.getItem(STATE);
  window.history.replaceState({}, "", redirectUri());
  forgetFlow();

  if (failure) {
    throw new SignInError(signInFailure(params.get("error_description"), failure));
  }
  if (!code || !verifier || params.get("state") !== expectedState) {
    throw new Error("this sign-in did not start here; try again");
  }
  writeTokens(
    await postToken(
      config,
      codeExchangeBody(config, { code, verifier, redirectUri: redirectUri() }),
    ),
  );
}

export function signedIn(): boolean {
  return readTokens() !== null;
}

/** A live ID token, renewed first if this one is about to expire. */
export async function idToken(config: SiteConfig): Promise<string> {
  const tokens = readTokens();
  if (!tokens) throw new Error("not signed in");
  if (!isExpired(tokens, Date.now())) return tokens.idToken;

  const refreshToken = tokens.refreshToken;
  if (!refreshToken) {
    writeTokens(null);
    throw new Error("this session has expired; sign in again");
  }
  try {
    const renewed = await postToken(config, refreshBody(config, refreshToken));
    // A refresh grant returns no new refresh token unless rotation is on.
    const next: Tokens = {
      ...renewed,
      refreshToken: renewed.refreshToken ?? refreshToken,
    };
    writeTokens(next);
    return next.idToken;
  } catch (error: unknown) {
    writeTokens(null);
    throw error;
  }
}
